import { useMemo, useRef } from "react";
import { DndContext, PointerSensor, TouchSensor, KeyboardSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Pencil, Trash2 } from "lucide-react";
import { money, fmtDate } from "@/lib/format";
import { filterMembers, groupMembers } from "@/lib/specialReport";

const TH = "px-4 py-3";
const isChurch = (id) => String(id).startsWith("church:");

function Payments({ m, admin, onPay }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {!m.payments.length && <span className="text-slate-400">—</span>}
      {m.payments.map((p) => {
        const label = <>{money(p.amount)} <span className="text-slate-500">· {fmtDate(p.date)}</span></>;
        return admin
          ? <button key={p.id} type="button" onClick={() => onPay(m, p)} title={p.receipt_no ? `Receipt ${p.receipt_no}` : "Edit payment"} className="rounded-full border border-slate-300 px-2.5 py-0.5 text-xs font-semibold hover:border-slate-500" data-testid={`special-payment-${p.id}`}>{label}</button>
          : <span key={p.id} className="rounded-full border border-slate-200 px-2.5 py-0.5 text-xs font-semibold" data-testid={`special-payment-${p.id}`}>{label}</span>;
      })}
      {admin && <button type="button" onClick={() => onPay(m, null)} className="rounded-full border border-dashed border-[hsl(var(--primary))] px-2.5 py-0.5 text-xs font-semibold text-[hsl(var(--primary))]" data-testid={`special-add-payment-${m.id}`}>+ Payment</button>}
    </div>
  );
}

function Row({ m, admin, onPay, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: m.id, disabled: !admin });
  const style = { transform: CSS.Translate.toString(transform), transition, position: "relative", zIndex: isDragging ? 10 : undefined };
  return (
    <tr ref={setNodeRef} style={style} className={`border-t border-slate-100 align-top ${isDragging ? "bg-amber-50 shadow-lg" : "bg-white"}`} data-testid={`special-row-${m.id}`}>
      <td className={`${TH} font-semibold`}>
        <div className="flex items-start gap-2">
          {admin && <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} className="mt-0.5 cursor-grab touch-none rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 active:cursor-grabbing" aria-label={`Drag ${m.name}`} data-testid={`special-drag-${m.id}`}><GripVertical className="h-4 w-4" /></button>}
          <span>{m.name}</span>
        </div>
      </td>
      <td className={TH}>{m.church_name}</td><td className={`${TH} font-mono`}>{money(m.goal)}</td>
      <td className={TH}><Payments m={m} admin={admin} onPay={onPay} /></td>
      <td className={`${TH} font-mono`} data-testid={`special-paid-${m.id}`}>{money(m.paid)}</td>
      <td className={`${TH} font-mono font-bold`} data-testid={`special-balance-${m.id}`}>{money(m.balance)}</td>
      <td className={`${TH} whitespace-nowrap text-right`}>{admin && <>
        <button type="button" onClick={() => onEdit(m)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100" aria-label="Edit" data-testid={`special-edit-${m.id}`}><Pencil className="h-4 w-4" /></button>
        <button type="button" onClick={() => onDelete(m)} className="rounded p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-700" aria-label="Delete" data-testid={`special-delete-${m.id}`}><Trash2 className="h-4 w-4" /></button></>}</td>
    </tr>
  );
}

function ChurchGroup({ cid, name, list, admin, children }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: `church:${cid}`, disabled: !admin });
  const style = { transform: CSS.Translate.toString(transform), transition, position: "relative", zIndex: isDragging ? 20 : undefined };
  return (
    <tbody ref={setNodeRef} style={style} className={isDragging ? "shadow-xl" : ""} data-testid={`special-group-${cid}`}>
      <tr className="border-t-2 border-slate-300 bg-[hsl(var(--sand))]">
        <td colSpan={7} className="px-4 py-2 font-heading text-sm font-bold uppercase tracking-wider text-slate-800" data-testid={`special-group-heading-${cid}`}>
          <div className="flex items-center gap-2">
            {admin && <button type="button" ref={setActivatorNodeRef} {...attributes} {...listeners} className="cursor-grab touch-none rounded p-0.5 text-slate-500 hover:bg-white/60 active:cursor-grabbing" aria-label={`Drag church ${name}`} data-testid={`special-church-drag-${cid}`}><GripVertical className="h-4 w-4" /></button>}
            <span>{name}</span> <span className="font-normal normal-case text-slate-500">· {list.length}</span>
          </div>
        </td>
      </tr>
      {children}
    </tbody>
  );
}

export function SpecialDetailsTable({ members, f, admin, onPay, onEdit, onDelete, onReorder, order = [], onReorderChurches }) {
  const shown = filterMembers(members, f);
  const groups = useMemo(() => groupMembers(shown, order), [shown, order]);
  const churchOf = useMemo(() => Object.fromEntries(members.map((m) => [m.id, m.church_id])), [members]);
  const churchRef = useRef(churchOf);
  churchRef.current = churchOf;
  const keepFor = (a) => (d) => (isChurch(a) ? isChurch(d.id) : !isChurch(d.id) && churchRef.current[d.id] === churchRef.current[a]);
  const keyGetter = (e, args) => {
    const all = args.context.droppableContainers;
    const only = all.getEnabled().filter(keepFor(args.active));
    const proxy = new Proxy(all, { get: (t, p) => (p === "getEnabled" ? () => only : typeof t[p] === "function" ? t[p].bind(t) : t[p]) });
    return sortableKeyboardCoordinates(e, { ...args, context: { ...args.context, droppableContainers: proxy } });
  };
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: keyGetter }));
  const collide = (args) => closestCenter({ ...args, droppableContainers: args.droppableContainers.filter(keepFor(args.active.id)) });
  const end = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const full = groupMembers(members, order);
    if (String(active.id).startsWith("church:")) {
      if (!String(over.id).startsWith("church:")) return;
      const ids = full.map(([c]) => `church:${c}`);
      onReorderChurches(arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id)).map((x) => x.slice(7)));
      return;
    }
    const g = full.find(([, , l]) => l.some((m) => m.id === active.id));
    if (!g || !g[2].some((m) => m.id === over.id)) return;
    const ids = g[2].map((m) => m.id);
    const moved = arrayMove(ids, ids.indexOf(active.id), ids.indexOf(over.id));
    onReorder(full.flatMap(([c, , l]) => (c === g[0] ? moved : l.map((m) => m.id))));
  };
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <DndContext sensors={sensors} collisionDetection={collide} onDragEnd={end}>
        <table className="w-full text-sm" data-testid="special-details-table">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{["Name", "Church Name", "Goal Amount", "Payments", "Paid", "Balance", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          {!members.length && <tbody><tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="special-empty">No people in this collection yet.</td></tr></tbody>}
          {!!members.length && !shown.length && <tbody><tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500" data-testid="special-no-match">No people match.</td></tr></tbody>}
          <SortableContext items={groups.map(([c]) => `church:${c}`)} strategy={verticalListSortingStrategy}>
            {groups.map(([c, name, list]) => (
              <ChurchGroup key={c} cid={c} name={name} list={list} admin={admin}>
                <SortableContext items={list.map((m) => m.id)} strategy={verticalListSortingStrategy}>
                  {list.map((m) => <Row key={m.id} m={m} admin={admin} onPay={onPay} onEdit={onEdit} onDelete={onDelete} />)}
                </SortableContext>
              </ChurchGroup>
            ))}
          </SortableContext>
        </table>
      </DndContext>
    </div>
  );
}
